import { formatDateTime, useLocale, useTranslation } from '@multaqa/i18n'
import { AppError, useAppErrorMessages } from '@multaqa/ui/components/async/app-error'
import { ListSkeleton } from '@multaqa/ui/components/async/list-skeleton'
import { Badge, type BadgeTone } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { toast } from '@multaqa/ui/components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@multaqa/ui/components/ui/table'
import { useState } from 'react'
import { contactLabel } from '@/lib/contact-label'
import { formatErrorMessage } from '@/lib/error-message'
import { localizedText } from '@/lib/localized-text'
import type { GroupInvitation } from './api'
import {
  useGroupCompanies,
  useGroupInvitations,
  useResendInvitation,
  useRevokeInvitation,
} from './api'

const STATUS_TONE: Record<GroupInvitation['status'], BadgeTone> = {
  pending: 'warning',
  expired: 'danger',
  accepted: 'success',
  revoked: 'neutral',
}

export function GroupInvitations({ groupId }: { groupId: string }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const messages = useAppErrorMessages()
  const invitations = useGroupInvitations(groupId)
  const companies = useGroupCompanies(groupId)
  const resend = useResendInvitation(groupId)
  const revoke = useRevokeInvitation(groupId)
  const [revoking, setRevoking] = useState<GroupInvitation | null>(null)

  if (invitations.isLoading) {
    return (
      <div data-testid="group-invitations-loading">
        <ListSkeleton columns={[2, 1.5, 1, 1, 1, 1.5]} rows={5} />
      </div>
    )
  }

  if (invitations.isError) {
    return (
      <div data-testid="group-invitations-error">
        <AppError error={invitations.error} messages={messages} retry={invitations.refetch} />
      </div>
    )
  }

  const list = invitations.data ?? []
  if (list.length === 0) {
    return (
      <div data-testid="group-invitations-empty">
        <EmptyState
          title={t('groups.invitations.emptyTitle')}
          description={t('groups.invitations.emptyBody')}
          className="border border-border py-8"
        />
      </div>
    )
  }

  const companyLabel = (companyId: string) => {
    const company = (companies.data ?? []).find((candidate) => candidate.id === companyId)
    return company ? localizedText(company.name, locale) : null
  }

  return (
    <section data-testid="group-invitations-panel" aria-labelledby="group-invitations-heading">
      <h2 id="group-invitations-heading" className="sr-only">
        {t('groups.invitations.title')}
      </h2>
      <Table data-testid="group-invitations-table" className="border border-border">
        <TableHeader>
          <TableRow className="bg-muted hover:bg-muted">
            <TableHead>{t('groups.invitations.columns.email')}</TableHead>
            <TableHead>{t('groups.invitations.columns.company')}</TableHead>
            <TableHead>{t('groups.invitations.columns.role')}</TableHead>
            <TableHead>{t('groups.invitations.columns.status')}</TableHead>
            <TableHead>{t('groups.invitations.columns.expires')}</TableHead>
            <TableHead className="text-end">{t('groups.invitations.columns.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.map((invitation) => {
            const label = companyLabel(invitation.company_id)
            const resending = resend.isPending && resend.variables === invitation.id
            const revokingRow = revoke.isPending && revoke.variables === invitation.id
            const rowBusy = resending || revokingRow

            return (
              <TableRow key={invitation.id} data-testid={`group-invitation-${invitation.id}`}>
                <TableCell className="text-small font-mono">
                  {invitation.name ? (
                    <span className="block font-sans">{invitation.name}</span>
                  ) : null}
                  <bdi dir="ltr">{contactLabel(invitation.email, invitation.phone)}</bdi>
                </TableCell>
                <TableCell className="text-body">
                  {label ?? <span className="font-mono">{invitation.company_id}</span>}
                </TableCell>
                <TableCell className="text-body">{invitation.system_role ?? '—'}</TableCell>
                <TableCell>
                  <Badge tone={STATUS_TONE[invitation.status]}>
                    {t(`groups.invitations.status.${invitation.status}`)}
                  </Badge>
                </TableCell>
                <TableCell className="text-identifier font-mono tabular-nums whitespace-nowrap">
                  {invitation.expires_at
                    ? formatDateTime(new Date(invitation.expires_at), locale)
                    : '—'}
                </TableCell>
                <TableCell className="text-end">
                  <div className="flex justify-end gap-2">
                    {invitation.email ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        loading={resending}
                        disabled={rowBusy}
                        onClick={() =>
                          resend.mutate(invitation.id, {
                            onSuccess: () =>
                              toast.success(
                                t('groups.invitations.resendSuccess', {
                                  email: invitation.email,
                                }),
                              ),
                            onError: (error) => toast.error(formatErrorMessage(error)),
                          })
                        }
                        data-testid={`group-invitation-resend-${invitation.id}`}
                      >
                        {t('groups.invitations.resend')}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={rowBusy}
                      onClick={() => setRevoking(invitation)}
                      data-testid={`group-invitation-revoke-${invitation.id}`}
                    >
                      {t('groups.invitations.revoke')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {revoking ? (
        <RevokeInvitationConfirm
          invitation={revoking}
          loading={revoke.isPending}
          onConfirm={() =>
            revoke.mutate(revoking.id, {
              onSuccess: () => {
                toast.success(t('groups.invitations.revokeSuccess', { email: revoking.email }))
                setRevoking(null)
              },
              onError: (error) => toast.error(formatErrorMessage(error)),
            })
          }
          onClose={() => setRevoking(null)}
        />
      ) : null}
    </section>
  )
}

function RevokeInvitationConfirm({
  invitation,
  loading,
  onConfirm,
  onClose,
}: {
  invitation: GroupInvitation
  loading: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="group-invitation-revoke-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('groups.invitations.revokeDialog.title', { email: invitation.email })}
          </DialogTitle>
          <DialogDescription>
            {t('groups.invitations.revokeDialog.description', { email: invitation.email })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={loading}
            disabled={loading}
            onClick={onConfirm}
            data-testid="group-invitation-revoke-confirm"
          >
            {t('groups.invitations.revokeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
